/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { Divider, Flex, Button, Stack, Accordion, CheckboxGroup, Spinner } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from '../../../models/hooks';
import { BusinessPayloadT, BusinessPositionT, BusinessQueryParamsT, BusinessT } from '../../calendar/types';
import PositionSearch from './PositionSearch';
import PositionBusinessList from './PositionBusinessList';

interface Props {
  handleCheckedPositions: (recruiterIds: BusinessPositionT[]) => void;
  listHeight?: string;
}

export default function PositionsTab({ handleCheckedPositions, listHeight }: Props) {
  const { t } = useTranslation('calendar');
  const isFirstRender = React.useRef(true);

  const fetchBusinesses = useStoreActions((s) => s.calendar.fetchBusinesses);
  const [businesses, setBusinesses] = React.useState<BusinessT[]>([]);
  const [businessPaginationState, setBusinessPaginationState] = React.useState<Omit<BusinessPayloadT, 'data'> | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const loaderRef = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [currentCheckedPositions, setCurrentCheckedPositions] = React.useState<BusinessPositionT[]>([]);

  const defaultQueryParams: BusinessQueryParamsT = {
    searchQuery: '',
    page: 1,
    limit: 10,
    sortBy: 'name',
    sortOrder: 'asc',
    searchByPosition: '',
    forCalendar: true,
  };
  const [queryParams, setQueryParams] = React.useState<BusinessQueryParamsT>(defaultQueryParams);
  const [positionSearchQuery, setPositionSearchQuery] = React.useState<string>('');

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setBusinesses([]);
    handleCheckedPositions([]);
    setCurrentCheckedPositions([]);
    setQueryParams((p) => ({ ...p, page: 1, searchQuery: positionSearchQuery }));
    // eslint-disable-next-line
  }, [positionSearchQuery]);

  React.useEffect(() => {
    setIsLoading(true);
    fetchBusinesses(queryParams)
      .then((res) => {
        setBusinesses((prev) => [...prev, ...res.data.data]);
        setBusinessPaginationState(res.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, [queryParams]);

  const handleObserver = React.useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (isLoading) return;
      const hasMore = !!businessPaginationState && businessPaginationState?.currentPage < businessPaginationState?.lastPage;
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading) {
        setQueryParams((p) => ({ ...p, page: (p?.page || /* istanbul ignore next */ 1) + 1 }));
      }
    },
    [businessPaginationState, isLoading],
  );

  React.useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: scrollContainerRef.current,
      rootMargin: '100px',
      threshold: 0,
    });
    // istanbul ignore else
    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => {
      // eslint-disable-next-line
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
    // eslint-disable-next-line
  }, [handleObserver]);

  function handleCheckedBusinesses(position: BusinessPositionT) {
    function toggleItem<T extends Record<string, any>>(list: T[], item: T, key: keyof T): T[] {
      const exists = list.some((obj) => obj[key] === item[key]);
      if (exists) {
        return list.filter((obj) => obj[key] !== item[key]);
      }
      return [...list, item];
    }

    const positions = toggleItem(currentCheckedPositions, position, 'id');
    setCurrentCheckedPositions(positions);
    handleCheckedPositions(positions);
  }

  return (
    <React.Fragment>
      <PositionSearch value={queryParams.searchQuery || ''} onChange={(value) => setPositionSearchQuery(value)} />

      <Divider borderColor="gray.100" />
      <Flex justifyContent="end" px={4} py={2}>
        <Button
          colorScheme="blue"
          variant="link"
          size="xs"
          data-testid="ClearBtn"
          onClick={() => {
            handleCheckedPositions([]);
            setCurrentCheckedPositions([]);
            setPositionSearchQuery('');
          }}
          _focus={{
            boxShadow: 'none',
          }}
        >
          {t('administration:calendar.sidebarPositionClearAll')}
        </Button>
      </Flex>
      <Divider borderColor="gray.100" />
      <Stack
        data-testid="RecruiterPositionListContainer"
        sx={{ minHeight: '0', height: listHeight || `calc(-505px + 100vh)`, overflowY: 'auto' }}
        ref={scrollContainerRef}
      >
        <CheckboxGroup value={currentCheckedPositions?.reduce<string[]>((p, c) => [...p, c.id], [])}>
          <Stack data-testid="RecruiterPositionList" p={[5, 4]} spacing={[1, 2]}>
            <Accordion allowMultiple>
              {businesses?.map((business) => (
                <PositionBusinessList business={business} key={business.id} onCheckChange={handleCheckedBusinesses} />
              ))}
            </Accordion>
          </Stack>
        </CheckboxGroup>
        {isLoading && (
          <Stack alignItems="center" paddingY={3} data-testid="loader-container">
            <Spinner thickness="1px" speed="0.65s" color="blue.500" size="sm" />
          </Stack>
        )}
        <div ref={loaderRef} data-testid="loader" />
      </Stack>
    </React.Fragment>
  );
}
